/**
 * Per-assignee workload bar chart. Each row is one assignee; the
 * horizontal bar is segmented (done / in_progress / not_started /
 * blocked) so the PM can spot "鈴木 is buried in not_started while
 * everyone else is shipping".
 *
 * Tailwind-only — no chart library — to honour the "no new deps"
 * constraint. Bars are flex children sized as a fraction of the row's
 * total so segments always sum to 100%.
 */
import type { AssigneeWorkloadRow } from '../../lib/dashboard';
import { AssigneeChip } from '../common/AssigneeChip';
import { SIGNAL_COLOR } from '../DagView/signal';

export interface AssigneeWorkloadChartProps {
  rows: readonly AssigneeWorkloadRow[];
}

export function AssigneeWorkloadChart({ rows }: AssigneeWorkloadChartProps): JSX.Element {
  if (rows.length === 0) {
    return (
      <div className="rounded-lg border border-slate-200 bg-white px-3 py-4 text-sm text-slate-500">
        担当者別のタスクはまだありません
      </div>
    );
  }
  return (
    <ul className="m-0 flex list-none flex-col gap-2 p-0">
      {rows.map((row) => {
        const total = Math.max(row.total, 1);
        const segments: Array<{ key: string; count: number; color: string }> = [
          { key: 'done', count: row.done, color: SIGNAL_COLOR.done },
          { key: 'in_progress', count: row.inProgress, color: SIGNAL_COLOR.in_progress },
          { key: 'blocked', count: row.blocked, color: SIGNAL_COLOR.blocked },
          { key: 'not_started', count: row.notStarted, color: SIGNAL_COLOR.not_started },
        ];
        return (
          <li
            key={row.assigneeId ?? '__unassigned__'}
            className="flex items-center gap-3 text-sm"
          >
            <div className="w-28 shrink-0 truncate">
              <AssigneeChip
                assignee={
                  row.colorIndex != null
                    ? { name: row.name, colorIndex: row.colorIndex }
                    : null
                }
                name={row.name}
              />
            </div>
            <div className="flex h-4 min-w-0 flex-1 overflow-hidden rounded-full bg-slate-100">
              {segments.map((seg) =>
                seg.count > 0 ? (
                  <div
                    key={seg.key}
                    className="h-full"
                    style={{
                      width: `${(seg.count / total) * 100}%`,
                      backgroundColor: seg.color,
                    }}
                    title={`${seg.key}: ${seg.count}`}
                  />
                ) : null,
              )}
            </div>
            <div className="w-24 shrink-0 text-right text-[11px] tabular-nums text-slate-600">
              {row.done}/{row.total} ({row.donePercent}%)
            </div>
          </li>
        );
      })}
    </ul>
  );
}
